/**
 * Post-install Shopify claim token — ferried out of the URL at boot.
 *
 * The BE redirects a finished install to
 * `/integrations?shopify=installed#claim=<token>`. The token is a bearer
 * credential: whoever holds it can bind that store to their brand.
 *
 * It arrives in the fragment because a fragment is never sent to a server, but
 * that only holds while the token stays there. Two things move it into a query
 * string, where it reaches access logs and request-URL traces:
 *
 *   - The protected-route guard redirects a signed-out merchant to
 *     `/login?redirect=<location.href>`, and `location.href` carries the hash —
 *     percent-encoded, so it is a query value. A merchant installing from the
 *     Shopify admin is usually signed out, so this is the common path.
 *   - Sentry, Replay and Clarity all record the page URL at init, which happens
 *     before any component mounts.
 *
 * So the token has to leave the URL synchronously at boot, before the router and
 * before the observability SDKs. Same shape and the same reason as captureVisitId
 * next door, which solves it for ?vid=.
 *
 * sessionStorage, not the `storage` helper: that wraps localStorage, which is
 * shared across tabs and persists indefinitely. A claim is one tab, one sitting.
 */
const KEY = 'discovr-brand-shopify-claim';

const isBrowser = (): boolean => typeof window !== 'undefined';

/** Survives the page but not a reload. Better than losing the token outright
 *  when storage is blocked (Safari private mode, embedded webviews). */
let memoryToken: string | undefined;

function stripClaimFragment(): void {
  try {
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search,
    );
  } catch {
    // Nothing left to try; never break boot over this.
  }
}

/**
 * Move #claim= out of the current URL into sessionStorage. Call once at app
 * boot, BEFORE the router runs and BEFORE Sentry/Clarity init.
 *
 * Scoped to the install redirect — `/integrations` AND `?shopify=installed`.
 * The param alone is no gate, since an attacker supplies it in their own link;
 * the PATH is what stops `#claim=` on some unrelated page being stashed and
 * then picked up later by an install whose fragment got dropped. Reaching the
 * dialog at all still requires sending the victim to /integrations, where it
 * names the store and waits for a click.
 *
 * replaceState never navigates, so the router boots on the clean URL either
 * way; `?shopify=` is left alone because the integrations tab still reads it to
 * decide which outcome to report.
 */
export function captureClaimToken(): void {
  if (!isBrowser()) return;
  const hash = window.location.hash;
  if (!hash.includes('claim=')) return;
  if (window.location.pathname !== '/integrations') return;
  if (new URLSearchParams(window.location.search).get('shopify') !== 'installed') return;

  const token = new URLSearchParams(hash.replace(/^#/, '')).get('claim');
  if (token) {
    try {
      window.sessionStorage.setItem(KEY, token);
    } catch {
      memoryToken = token;
    }
  }
  // Always, even if nothing could be stored: a merchant who cannot claim beats
  // a bearer token left in the address bar.
  stripClaimFragment();
}

export function readClaimToken(): string | undefined {
  if (!isBrowser()) return memoryToken;
  try {
    return window.sessionStorage.getItem(KEY) ?? memoryToken;
  } catch {
    return memoryToken;
  }
}

/**
 * Drop the token once it has been claimed or declined. Single-use: leaving it
 * would re-offer the confirmation dialog on every visit to the tab for as long
 * as the tab lives.
 */
export function clearClaimToken(): void {
  memoryToken = undefined;
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
